import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Icon from 'components/AppIcon';
import * as passwordApi from 'shared/api/auth/passwordApi';

/**
 * Страница восстановления пароля
 * Поддерживает два режима:
 * 1. Запрос сброса пароля (ввод email)
 * 2. Подтверждение сброса (ввод нового пароля с токеном из URL)
 */
const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Определяем режим: запрос или подтверждение
  const isConfirmMode = !!token;

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await passwordApi.requestPasswordReset({ email });
      setSuccess('Инструкции по сбросу пароля отправлены на ваш email');
      setEmail('');
    } catch (err) {
      console.error('Password reset request failed:', err);
      if (err.status === 404) {
        setError('Пользователь с таким email не найден');
      } else {
        setError(err.message || 'Не удалось отправить запрос на сброс пароля');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (newPassword.length < 8) {
      setError('Пароль должен содержать минимум 8 символов');
      return;
    }

    setIsLoading(true);

    try {
      await passwordApi.confirmPasswordReset({
        token,
        newPassword
      });
      setSuccess('Пароль успешно изменён! Сейчас вы будете перенаправлены на страницу входа.');
      
      setTimeout(() => {
        navigate('/login-screen', {
          state: {
            message: 'Пароль успешно изменён. Войдите с новым паролем.',
            type: 'success'
          }
        });
      }, 2000);
    } catch (err) {
      console.error('Password reset confirm failed:', err);
      if (err.status === 400) {
        setError('Недействительная или истёкшая ссылка для сброса пароля');
      } else {
        setError(err.message || 'Не удалось сбросить пароль');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="KeyRound" size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-text-primary mb-2">
            {isConfirmMode ? 'Новый пароль' : 'Восстановление пароля'}
          </h1>
          <p className="text-text-secondary">
            {isConfirmMode 
              ? 'Введите новый пароль для вашего аккаунта'
              : 'Введите email для получения инструкций по сбросу пароля'
            }
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-surface rounded-xl shadow-lg border border-secondary-200 p-8">
          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-md">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0" />
                <p className="text-sm text-success">{success}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-md">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
                <p className="text-sm text-error">{error}</p>
              </div>
            </div>
          )}

          {/* Request Reset Form */}
          {!isConfirmMode && (
            <form onSubmit={handleRequestReset} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                  Email адрес
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="example@company.com"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <Icon name="Mail" size={16} className="text-secondary-400" />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email}
                className={`
                  w-full py-3 rounded-md font-medium transition-all duration-150
                  ${isLoading || !email
                    ? 'bg-secondary-200 text-secondary-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary-700 hover-lift'
                  }
                `}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    Отправка...
                  </div>
                ) : (
                  'Отправить инструкции'
                )}
              </button>
            </form>
          )}

          {/* Confirm Reset Form */}
          {isConfirmMode && (
            <form onSubmit={handleConfirmReset} className="space-y-6">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-text-primary mb-2">
                  Новый пароль
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Минимум 8 символов"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} className="text-secondary-400" />
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">
                  Подтвердите пароль
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Повторите пароль"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !newPassword || !confirmPassword}
                className={`
                  w-full py-3 rounded-md font-medium transition-all duration-150
                  ${isLoading || !newPassword || !confirmPassword
                    ? 'bg-secondary-200 text-secondary-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary-700 hover-lift'
                  }
                `}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    Сохранение...
                  </div>
                ) : (
                  'Сохранить новый пароль'
                )}
              </button>
            </form>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login-screen"
              className="text-sm text-primary hover:text-primary-700 font-medium transition-colors duration-150 inline-flex items-center gap-2"
            >
              <Icon name="ArrowLeft" size={16} />
              Вернуться к входу
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
